import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress, 
  Card, 
  CardContent, 
  Divider,
  LinearProgress,
  TextField,
  Grid
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import StorageIcon from '@mui/icons-material/Storage';
import axios from 'axios';

const ExportData = () => {
  const [loading, setLoading] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleComprehensiveExport = async () => {
    setLoading('comprehensive');
    setMessage('');
    setError('');
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await axios.get('/api/admin/export/comprehensive?' + params.toString(), {
        responseType: 'blob',
      });
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `backup-completo-${timestamp}.zip`;
      downloadFile(response.data, filename);
      
      setMessage('✅ Backup completo scaricato! ZIP con tutti i dati esportati.');
    } catch (err) {
      setError('❌ Errore durante l\'esportazione: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading('');
    }
  };

  const handleDatabaseBackup = async () => {
    setLoading('database');
    setMessage('');
    setError('');
    try {
      const response = await axios.get('/api/admin/backup/download', {
        responseType: 'blob',
      });
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `dev.db-backup-${timestamp}.db`;
      downloadFile(response.data, filename);
      
      setMessage('✅ Backup database scaricato! File SQLite pronto per il ripristino.');
    } catch (err) {
      setError('❌ Errore durante il download: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading('');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>🛡️ Backup & Esportazione Dati</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Crea backup completi della piattaforma per disaster recovery e ripristino.
      </Typography>

      {/* OPTION 1: COMPREHENSIVE CSV EXPORT */}
      <Card sx={{ mb: 3, border: '2px solid #1976d2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DownloadIcon sx={{ mr: 2, fontSize: 32, color: '#1976d2' }} />
            <Typography variant="h6">Opzione 1: Export CSV Completo (Consigliato)</Typography>
          </Box>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Esporta TUTTI i dati (utenti, aziende, offerte, candidature, relazioni) in 13 file CSV.
            Ideale per backup, analisi, o trasferimento su nuova piattaforma.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Incluso:</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2, pl: 2 }}>
            ✓ Tutti i profili utenti (CV path, categorie, corsi)<br/>
            ✓ Tutti i dati aziendali (settore, tipo, categorie)<br/>
            ✓ Tutte le offerte di lavoro e candidature<br/>
            ✓ Relazioni M2M (utenti-categorie, job-categorie, ecc.)<br/>
            ✓ Account admin, codici di accesso, token reset<br/>
            ✓ Istruzioni di ripristino in README<br/>
          </Typography>

          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Filtri opzionali per data:</strong>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Data inizio"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Data fine"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={loading === 'comprehensive' ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleComprehensiveExport}
            disabled={loading !== ''}
            size="large"
          >
            {loading === 'comprehensive' ? 'Esportazione in corso...' : 'Scarica Backup CSV Completo'}
          </Button>
          {loading === 'comprehensive' && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#666' }}>
                ⏳ Elaborazione in corso (questo può richiedere alcuni minuti per grandi volumi)...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* OPTION 2: RAW DATABASE BACKUP */}
      <Card sx={{ mb: 3, border: '2px solid #ff9800' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorageIcon sx={{ mr: 2, fontSize: 32, color: '#ff9800' }} />
            <Typography variant="h6">Opzione 2: Backup Database Grezzo (Più Veloce)</Typography>
          </Box>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Scarica una copia diretta del file SQLite. Opzione più veloce e affidabile 
            per disaster recovery completo (include anche resume file metadata).
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Vantaggi:</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2, pl: 2 }}>
            ✓ Download più veloce (no elaborazione)<br/>
            ✓ 100% fedele al database originale<br/>
            ✓ Perfetto per ripristino su nuova istanza<br/>
            ✓ Include metadati e integrità<br/>
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Nota:</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Questo è il file SQLite (.db) grezzo. Per ripristinare, copia il file 
            come <code>dev.db</code> nella cartella <code>/backend/prisma/</code>.
            Ricorda di fare backup anche dei CV in <code>/public/resumes/</code>.
          </Typography>

          <Button
            variant="contained"
            color="warning"
            startIcon={loading === 'database' ? <CircularProgress size={20} /> : <StorageIcon />}
            onClick={handleDatabaseBackup}
            disabled={loading !== ''}
            size="large"
          >
            {loading === 'database' ? 'Download in corso...' : 'Scarica Backup Database (.db)'}
          </Button>
          {loading === 'database' && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* LEGACY OPTION */}
      <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
            📋 <strong>Nota: Export CSV semplice (legacy) ancora disponibile</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            L'endpoint <code>/api/admin/export/csv</code> rimane per compatibilità.
            Usa le opzioni sopra per backup più completi.
          </Typography>
        </CardContent>
      </Card>

      {/* MESSAGES */}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* RESTORATION INSTRUCTIONS */}
      <Card sx={{ backgroundColor: '#e8f5e9' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>📖 Istruzioni di Ripristino</Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            <strong>Da Backup CSV (Export Completo):</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2, pl: 2 }}>
            1. Estrai il ZIP scaricato<br/>
            2. Leggi il README_IT.txt per l'ordine di importazione<br/>
            3. Importa i CSV nei tuoi strumenti di database (SQLiteStudio, DBeaver, ecc.)<br/>
            4. Verifica l'integrità dei dati
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            <strong>Da Backup Database (.db):</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2, pl: 2 }}>
            1. Stoppa il server: <code>npm stop</code><br/>
            2. Backup il file attuale: <code>cp backend/prisma/dev.db backend/prisma/dev.db.old</code><br/>
            3. Copia il file scaricato: <code>cp dev.db-backup-*.db backend/prisma/dev.db</code><br/>
            4. Riavvia il server: <code>npm start</code><br/>
            5. Verifica la piattaforma
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            ⚠️ <strong>Importante:</strong> Fai backup anche dei CV in <code>/public/resumes/</code> 
            e di qualsiasi altra cartella con dati persistenti. Questi endpoint esportano solo il database.
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExportData;
